// Layout — Smart Layout Selector with NEW_NAV_V2 feature flag.
//
// This file replaces the original Layout.tsx with a thin wrapper that:
//   - Default: renders the existing Layout (now named LayoutLegacy)
//   - When NEW_NAV_V2 = true: lazy-loads and renders LayoutV2
//
// All existing pages keep importing `Layout from '@/components/Layout'` — 
// they don't need to know which layout actually rendered.
//
// Rollout:
//   - Aletlala tenant (id=1): NEW_NAV_V2 = true
//   - amr@aletlala.com: NEW_NAV_V2 = true
//   - All other users: legacy Layout
//
// Rollback: edit feature-flags.ts and set NEW_NAV_V2 = false.

import { ReactNode, lazy, Suspense } from 'react'
import { Loader2 } from 'lucide-react'
import { useFeatureFlag } from '@/lib/feature-flags'
import LayoutLegacy from './LayoutLegacy'
// === Phase 2 Wave 6 Agent S3 BEGIN ===
import IdleTimerProvider from './Session/IdleTimerProvider'
// === Phase 2 Wave 6 Agent S3 END ===

const LayoutV2 = lazy(() => import('./LayoutV2'))

interface LayoutProps {
  children: ReactNode
}

function LayoutLoader() {
  return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
    </div>
  )
}

export default function Layout({ children }: LayoutProps) {
  const useNewNav = useFeatureFlag('NEW_NAV_V2')

  // === Phase 2 Wave 6 Agent S3 BEGIN ===
  // Wrap whichever layout we pick in IdleTimerProvider so the session
  // idle-timeout + warning modal apply uniformly. The provider is a
  // no-op for unauthenticated views (it short-circuits when there's no
  // access_token in localStorage).
  const inner = useNewNav ? (
    <Suspense fallback={<LayoutLoader />}>
      <LayoutV2>{children}</LayoutV2>
    </Suspense>
  ) : (
    <LayoutLegacy>{children}</LayoutLegacy>
  )

  return <IdleTimerProvider>{inner}</IdleTimerProvider>
  // === Phase 2 Wave 6 Agent S3 END ===
}
